"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from "react"
import { X } from "lucide-react"
import type { DayLedgerResponse } from "@bitebalance/contracts"
import { useAmyDataRefresh } from "@/lib/useAmyDataRefresh"
import { useApiClient } from "@/api/useApiClient"
import { openAmyWithPrompt } from "@/lib/amyLauncher"
import { formatKcal, formatWholeNumber, useI18n, type I18nContextValue, type Locale } from "@/i18n"
import { humanizeRecipeId } from "@/components/plan/recipeDisplay"

type TFn = I18nContextValue["t"]

const KNOWN_SLOTS = new Set([
  "breakfast",
  "morning_snack",
  "lunch",
  "afternoon_snack",
  "dinner",
  "snack",
])

/** Slot name for copy; an unknown wire slot is humanized, never a raw key. */
function slotName(slot: string, t: TFn): string {
  return KNOWN_SLOTS.has(slot) ? t(`views.slots.${slot}`) : humanizeRecipeId(slot)
}

const REASON_KEY: Record<
  NonNullable<DayLedgerResponse["projectionDelta"]>["reasons"][number]["outcome"],
  string
> = {
  partial: "views.today.projectionReasonPartial",
  skipped: "views.today.projectionReasonSkipped",
  changed: "views.today.projectionReasonChanged",
  had: "views.today.projectionReasonHad",
}

/**
 * The ONE planned-vs-projected explanation line (2026-09-04): built purely
 * from the server's `projectionDelta` (sign + reasons) and `plannedTodayKcal`
 * — no inference. Null when the projection equals the plan or the server
 * didn't serve the fields (older payloads / idempotent replays).
 */
export function projectionExplanation(
  ledger: DayLedgerResponse,
  t: TFn,
  locale: Locale
): string | null {
  const delta = ledger.projectionDelta
  if (!delta || delta.kcal === 0 || ledger.plannedTodayKcal === undefined) return null
  const reasons = delta.reasons.map(r => t(REASON_KEY[r.outcome], { slot: slotName(r.slot, t) }))
  if (delta.offPlanKcal > 0) reasons.push(t("views.today.projectionReasonOffPlan"))
  if (reasons.length === 0) return null
  return t(delta.kcal < 0 ? "views.today.projectionLower" : "views.today.projectionHigher", {
    planned: formatWholeNumber(ledger.plannedTodayKcal, locale),
    reasons: reasons.join(t("views.today.projectionReasonJoin")),
  })
}

/**
 * ADR-035 — the reality line under the plan: eaten so far, projected total
 * and the day's verdict, straight from GET /v1/ledger/today (all math
 * server-side, never re-derived here). Off-plan entries logged with Amy
 * appear with their confidence and can be removed (mislogs are normal).
 * Silently absent while the ledger has nothing to say (no outcomes, no
 * off-plan food) — a second empty bar would be noise.
 *
 * 2026-09-04 (founder acceptance: "364 kcal of room left" while the day was
 * projected BELOW the safe floor): the verdict follows the server-owned
 * `dayState` with precedence ABOVE any positive "room left" — below_floor →
 * calm notice with the SERVER's deficit; above_target → over; between →
 * headroomKcal; on_target → exact. Without `dayState` (older payloads) the
 * legacy branch applies, but "room left" is never shown when
 * `projectedBelowFloor` is served true.
 */
export function LedgerStrip() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [ledger, setLedger] = useState<DayLedgerResponse | null>(null)

  const load = useCallback(() => {
    api
      .getDayLedger()
      .then(setLedger)
      .catch(() => {
        /* reality line is enhancement — the planned bar above still stands */
      })
  }, [api])

  useEffect(() => {
    load()
  }, [load])

  // ADR-036 finding 6: food Amy logs in chat appears here immediately.
  useAmyDataRefresh(load)

  async function removeEntry(id: string) {
    try {
      const updated = await api.deleteOffPlanEntry(id)
      setLedger(updated)
    } catch {
      /* leave the entry visible — deleting again is cheap */
    }
  }

  if (!ledger) return null
  const hasActivity = ledger.consumedKcal > 0 || ledger.offPlanEntries.length > 0
  if (!hasActivity) return null

  const dayState = ledger.dayState ?? null
  // Over-target amount, server-served: targetAdaptation.deltaToTargetKcal
  // (projected − target) when present; older payloads carry the same
  // quantity as the magnitude of a negative flexibleKcal (target − projected).
  const overBy =
    dayState !== null
      ? dayState.state === "above_target"
        ? overAmount(ledger)
        : 0
      : ledger.targetKcal !== null && ledger.projectedKcal > ledger.targetKcal
        ? overAmount(ledger)
        : 0

  let verdict: "below_floor" | "above_target" | "headroom" | "on_target" | "none"
  let headroom: number | null = null
  if (dayState !== null) {
    if (dayState.state === "below_floor") verdict = "below_floor"
    else if (dayState.state === "above_target") verdict = overBy > 0 ? "above_target" : "none"
    else if (dayState.state === "below_target_above_floor") {
      headroom = dayState.headroomKcal
      verdict = headroom !== null && headroom > 0 ? "headroom" : "none"
    } else verdict = "on_target"
  } else if (overBy > 0) {
    verdict = "above_target"
  } else if (
    ledger.projectedBelowFloor !== true &&
    ledger.flexibleKcal !== null &&
    ledger.flexibleKcal > 0
  ) {
    headroom = ledger.flexibleKcal
    verdict = "headroom"
  } else {
    verdict = "none"
  }

  const explanation = projectionExplanation(ledger, t, locale)

  return (
    <div className="flex flex-col gap-2 rounded-bb-xl border border-line bg-paper p-4 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span className="text-ink-2">
          {t("views.today.ledgerEaten")}{" "}
          <strong className="text-ink">{formatKcal(ledger.consumedKcal, locale)}</strong>
        </span>
        <span className="text-ink-2">
          {t("views.today.ledgerProjected")}{" "}
          <strong className={verdict === "above_target" ? "text-danger" : "text-ink"}>
            {formatKcal(ledger.projectedKcal, locale)}
          </strong>
          {ledger.targetKcal !== null && (
            <span className="text-muted-foreground"> / {formatKcal(ledger.targetKcal, locale)}</span>
          )}
        </span>
      </div>

      {explanation && <p className="text-xs text-ink-2">{explanation}</p>}

      {verdict === "below_floor" && dayState && (
        <div
          role="status"
          className="flex flex-col gap-1.5 rounded-bb-md border border-gold/60 bg-gold/10 p-3"
        >
          <p className="text-sm font-semibold text-ink">{t("views.today.dayBelowFloorTitle")}</p>
          {dayState.floorDeficitKcal !== null && dayState.applicableFloorKcal !== null && (
            <p className="text-xs text-ink-2">
              {t("views.today.dayBelowFloorBody", {
                projected: formatWholeNumber(ledger.projectedKcal, locale),
                deficit: formatWholeNumber(dayState.floorDeficitKcal, locale),
                floor: formatWholeNumber(dayState.applicableFloorKcal, locale),
              })}
            </p>
          )}
          <button
            type="button"
            onClick={() => openAmyWithPrompt(t("views.today.dayBelowFloorPrompt"))}
            className="w-fit rounded-bb-full border border-line bg-paper px-3 py-1 text-xs font-medium text-green hover:bg-cream"
          >
            {t("views.today.dayBelowFloorCta")}
          </button>
        </div>
      )}

      {verdict === "above_target" && (
        <p className="text-xs font-medium text-danger">
          {t("views.today.ledgerOverProjection", { delta: formatWholeNumber(overBy, locale) })}
        </p>
      )}

      {verdict === "headroom" && headroom !== null && (
        <p className="text-xs text-ink-2">
          {t("views.today.ledgerFits", { kcal: formatWholeNumber(headroom, locale) })}
        </p>
      )}

      {verdict === "on_target" && (
        <p className="text-xs text-ink-2">{t("views.today.dayOnTarget")}</p>
      )}

      {ledger.dinnerAtRisk && (
        <p className="text-xs font-medium text-warning">{t("views.today.ledgerDinnerAtRisk")}</p>
      )}

      {ledger.offPlanEntries.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-line pt-2">
          <p className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
            {t("views.today.ledgerLoggedTitle")}
          </p>
          <ul className="flex flex-col gap-1">
            {ledger.offPlanEntries.map(entry => (
              <li key={entry.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate text-ink">{entry.description}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-ink-2">
                    {formatKcal(entry.kcal, locale)}
                    {entry.confidence !== "high" && (
                      <span className="text-muted-foreground"> ~</span>
                    )}
                  </span>
                  <button
                    type="button"
                    aria-label={t("views.today.ledgerDeleteLabel")}
                    onClick={() => void removeEntry(entry.id)}
                    className="rounded-bb-sm p-1 text-muted-foreground transition-colors hover:text-danger"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * The served over-target amount. `targetAdaptation.deltaToTargetKcal` IS
 * projected − target (services/planning ledger owner); older payloads
 * without it carry the same quantity as a negative `flexibleKcal`
 * (target − projected). Magnitude only — no client subtraction.
 */
function overAmount(ledger: DayLedgerResponse): number {
  const delta = ledger.targetAdaptation?.deltaToTargetKcal
  if (typeof delta === "number") return delta > 0 ? delta : 0
  if (ledger.flexibleKcal !== null && ledger.flexibleKcal < 0) return Math.abs(ledger.flexibleKcal)
  return 0
}
